import { v4 as uuid } from 'uuid';

import { format, getZonedDate } from '@eika-infrastruktur/date';
import ValutaFilter from '@eika-infrastruktur/ui-valuta-filter';
import { FormattedTransaction, FormattedTransactions } from '@eika/transaction-list/lib/TransactionList';

import { BookedTransaction, PendingTransaction } from '../generated-types';

const getDate = (transaction: FormattedTransaction) => {
    return getZonedDate(transaction.date);
};

const getPreviousTransactionDate = (transactions: FormattedTransaction[], index: number) => {
    const previous = index - 1;
    if (previous < 0 || transactions[previous] === null) return null;
    return getDate(transactions[previous]);
};

const isNewYearGroup = (
    transaction: FormattedTransaction,
    transactions: FormattedTransaction[],
    index: number,
): boolean => {
    const prevDate = getPreviousTransactionDate(transactions, index);
    const date = getDate(transaction);
    return prevDate === null || date.getFullYear() !== prevDate.getFullYear();
};

const getYearLabel = (transaction: FormattedTransaction) => {
    return format(getDate(transaction), 'yyyy');
};

const addUniqueKeyTransactions = (transactions: FormattedTransaction[]) => {
    return transactions.reduce((list, current) => {
        if (list.some((t) => t.id === current.id)) {
            return list.concat({ ...current, key: uuid() });
        }
        return list.concat(current);
    }, [] as FormattedTransaction[]);
};

const addUniqueKey = (month: FormattedTransactions) => ({
    ...month,
    transactions: addUniqueKeyTransactions(month.transactions),
});

const formatPendingTransaction = (pendingTransaction: PendingTransaction): FormattedTransaction => ({
    id: pendingTransaction.id,
    description: pendingTransaction.description,
    date: pendingTransaction.transactionDate,
    amount: ValutaFilter(pendingTransaction.postAmount),
    deposit: false,
    reservation: true,
    onClick: undefined,
    balance: undefined,
});

const formatBookedTransaction = (bookedTransaction: BookedTransaction): FormattedTransaction => ({
    id: bookedTransaction.id,
    description: bookedTransaction.description,
    date: bookedTransaction.transactionDate,
    amount: ValutaFilter(bookedTransaction.postAmount),
    deposit: false,
    reservation: false,
    onClick: undefined,
    balance: undefined,
    subDescription: bookedTransaction.categoryDescription,
});

export default function formatTransactions(
    pendingTransactions: PendingTransaction[] | undefined,
    bookedTransactions: BookedTransaction[],
): FormattedTransactions[] {
    const initialValue: FormattedTransactions[] = [];

    const combinedTransactions = [
        ...(pendingTransactions ? pendingTransactions.map((transaction) => formatPendingTransaction(transaction)) : []),
        ...bookedTransactions.map((transaction) => formatBookedTransaction(transaction)),
    ];

    return combinedTransactions
        .reduce((map, transaction, currentIndex, orgTrans) => {
            if (isNewYearGroup(transaction, orgTrans, currentIndex)) {
                const yearLabel = getYearLabel(transaction);
                map.push({ id: yearLabel, label: yearLabel, transactions: [] });
            }
            map[map.length - 1].transactions.push(transaction);
            return map;
        }, initialValue)
        .map(addUniqueKey)
        .map((month) => {
            return {
                ...month,
                transactions: month.transactions.map((transaction) => ({
                    ...transaction,
                    date: format(getDate(transaction), 'dd.MM'),
                })),
            };
        });
}
